import type {
  BosObjectStorageConfig,
  ObsObjectStorageConfig,
  ObjectStorageConfig,
  OssObjectStorageConfig,
  QiniuObjectStorageConfig,
  S3ObjectStorageConfig,
  StorageAdapter,
  UploadParams,
  UploadResult,
} from '../type';
import { ObjectStorageType } from '../type';
import { assertBucketName } from '../utils/utils';
import {
  createBaiduBosInstance,
  upload as baiduBosUpload,
} from './baiduBosUpload';
import { upload as normalUpload } from './normalUpload';
import { upload as obsUpload } from './obsUpload';
import { upload as ossUpload } from './ossUpload';
import { upload as qiniuUpload } from './qiniuUpload';
import { upload as s3Upload } from './s3Upload';

type SdkConstructor<T = unknown> = new (options: Record<string, unknown>) => T;

interface S3SdkLike {
  S3: new (options?: Record<string, unknown>) => unknown;
}

interface QiniuSdkLike {
  upload: (...args: unknown[]) => unknown;
}

abstract class BaseStorageAdapter<TInstance = unknown>
  implements StorageAdapter<TInstance>
{
  abstract readonly type: ObjectStorageType;
  abstract readonly instance: TInstance;

  abstract upload(params: UploadParams): Promise<UploadResult>;
}

/**
 * normal 上传不创建第三方 SDK 客户端，直接委托给 XHR 上传逻辑。
 */
class NormalStorageAdapter extends BaseStorageAdapter<undefined> {
  readonly type = ObjectStorageType.NORMAL;
  readonly instance = undefined;

  upload(params: UploadParams): Promise<UploadResult> {
    return normalUpload(params);
  }
}

/**
 * 华为云 OBS 适配器负责将统一配置映射为 OBS SDK 构造参数。
 */
class ObsStorageAdapter<
  TInstance = unknown
> extends BaseStorageAdapter<TInstance> {
  readonly type = ObjectStorageType.OBS;
  readonly instance: TInstance;
  private readonly bucketName: string;

  constructor(config: ObsObjectStorageConfig) {
    super();
    const sdkOtherParams = config.sdkOtherParams ?? {};
    const ObsSdk = requireSdkObject<SdkConstructor>(
      config.sdkObject,
      ObjectStorageType.OBS
    );

    this.bucketName = assertBucketName(config.bucketName);
    this.instance = new ObsSdk({
      ...sdkOtherParams,
      access_key_id: config.accessKey,
      secret_access_key: config.secretKey,
      server: requireEndPoint(config.endPoint),
      ...(config.securityToken ? { security_token: config.securityToken } : {}),
    }) as TInstance;
  }

  upload(params: UploadParams): Promise<UploadResult> {
    return obsUpload(this.instance, params, this.bucketName);
  }
}

/**
 * 阿里云 OSS 适配器负责 OSS 客户端创建和上传委托。
 */
class OssStorageAdapter<
  TInstance = unknown
> extends BaseStorageAdapter<TInstance> {
  readonly type = ObjectStorageType.OSS;
  readonly instance: TInstance;

  constructor(config: OssObjectStorageConfig) {
    super();
    const endPoint = requireEndPoint(config.endPoint);
    const sdkOtherParams = config.sdkOtherParams ?? {};
    const OssSdk = requireSdkObject<SdkConstructor>(
      config.sdkObject,
      ObjectStorageType.OSS
    );

    this.instance = new OssSdk({
      ...sdkOtherParams,
      accessKeyId: config.accessKey,
      accessKeySecret: config.secretKey,
      authorizationV4: true,
      endpoint: endPoint,
      bucket: assertBucketName(config.bucketName),
      region: resolveOssRegion(endPoint, config.region),
      ...(config.securityToken ? { stsToken: config.securityToken } : {}),
    }) as TInstance;
  }

  upload(params: UploadParams): Promise<UploadResult> {
    return ossUpload(this.instance, params);
  }

  cancel(): void {
    const maybeCancelable = this.instance as { cancel?: () => void };
    maybeCancelable.cancel?.();
  }
}

/**
 * AWS S3 和 MinIO 共用 AWS SDK v2 的 S3 客户端形态。
 */
class S3StorageAdapter<
  TInstance = unknown
> extends BaseStorageAdapter<TInstance> {
  readonly type: ObjectStorageType.MINIO | ObjectStorageType.AWS;
  readonly instance: TInstance;
  private readonly bucketName: string;

  constructor(config: S3ObjectStorageConfig) {
    super();
    const sdkOtherParams = config.sdkOtherParams ?? {};
    const sdkObject = requireSdkObject<S3SdkLike>(
      config.sdkObject,
      config.type
    );

    this.type = config.type as ObjectStorageType.MINIO | ObjectStorageType.AWS;
    this.bucketName = assertBucketName(config.bucketName);
    this.instance = new sdkObject.S3({
      ...sdkOtherParams,
      accessKeyId: config.accessKey,
      secretAccessKey: config.secretKey,
      endpoint: config.endPoint,
      region: config.region,
      signatureVersion: 'v4',
      ...(config.type === ObjectStorageType.MINIO
        ? { s3ForcePathStyle: true }
        : {}),
      ...(config.securityToken ? { sessionToken: config.securityToken } : {}),
    }) as TInstance;
  }

  upload(params: UploadParams): Promise<UploadResult> {
    return s3Upload(this.instance, params, this.bucketName);
  }
}

/**
 * 七牛云适配器使用上传 token 调用 Kodo JS SDK。
 */
class QiniuStorageAdapter<
  TInstance = unknown
> extends BaseStorageAdapter<TInstance> {
  readonly type = ObjectStorageType.QINIU;
  readonly instance: TInstance;
  private readonly uploadToken: string | undefined;
  private readonly sdkOtherParams: Record<string, unknown>;

  constructor(config: QiniuObjectStorageConfig) {
    super();
    this.instance = requireSdkObject<QiniuSdkLike>(
      config.sdkObject,
      ObjectStorageType.QINIU
    ) as TInstance;
    this.uploadToken = config.uploadToken;
    this.sdkOtherParams = config.sdkOtherParams ?? {};
  }

  upload(params: UploadParams): Promise<UploadResult> {
    return qiniuUpload(
      this.instance,
      params,
      this.uploadToken,
      this.sdkOtherParams
    );
  }
}

/**
 * 百度 BOS 适配器负责创建 BosClient，并支持服务端签名覆盖。
 */
class BosStorageAdapter<
  TInstance = unknown
> extends BaseStorageAdapter<TInstance> {
  readonly type = ObjectStorageType.BOS;
  readonly instance: TInstance;
  private readonly bucketName: string;

  constructor(config: BosObjectStorageConfig) {
    super();
    this.bucketName = assertBucketName(config.bucketName);
    this.instance = createBaiduBosInstance<TInstance>(config.sdkObject, {
      accessKey: config.accessKey,
      secretKey: config.secretKey,
      endPoint: requireEndPoint(config.endPoint),
      signatureUrl: config.signatureUrl,
      sdkOtherParams: config.sdkOtherParams,
    });
  }

  upload(params: UploadParams): Promise<UploadResult> {
    return baiduBosUpload(this.instance, params, this.bucketName);
  }
}

/**
 * 根据 config.type 创建对应的存储适配器。
 */
export function createStorageAdapter<TInstance, TSdkObject>(
  config: ObjectStorageConfig<TSdkObject>
): StorageAdapter<TInstance> {
  switch (config.type) {
    case ObjectStorageType.NORMAL:
      return new NormalStorageAdapter() as StorageAdapter<TInstance>;

    case ObjectStorageType.OBS:
      return new ObsStorageAdapter<TInstance>(config);

    case ObjectStorageType.OSS:
      return new OssStorageAdapter<TInstance>(config);

    case ObjectStorageType.MINIO:
    case ObjectStorageType.AWS:
      return new S3StorageAdapter<TInstance>(config);

    case ObjectStorageType.QINIU:
      return new QiniuStorageAdapter<TInstance>(config);

    case ObjectStorageType.BOS:
      return new BosStorageAdapter<TInstance>(config);

    default:
      throw new Error(
        `ObjectStorage: unsupported type "${(config as { type: string }).type}"`
      );
  }
}

/**
 * 确保云厂商上传由业务方显式传入 SDK，避免本包内置厂商 SDK。
 */
function requireSdkObject<T>(sdkObject: unknown, type: ObjectStorageType): T {
  if (sdkObject == null) {
    throw new Error(`ObjectStorage: sdkObject is required for type "${type}"`);
  }
  return sdkObject as unknown as T;
}

/**
 * 规范化 endpoint 输入，并在 SDK 必需 endpoint 时提前失败。
 */
function requireEndPoint(endPoint?: string): string {
  const resolved = endPoint?.trim();
  if (!resolved) {
    throw new Error('ObjectStorage: endPoint is required');
  }
  return resolved;
}

/**
 * OSS v4 授权需要 region；调用方未显式传入时，从常见 endpoint 格式推断。
 */
function resolveOssRegion(endPoint: string, region?: string): string {
  if (region) {
    return region;
  }

  const match = endPoint.match(/https?:\/\/([a-zA-Z0-9-]+)\.aliyuncs\.com/);
  if (match?.[1]) {
    return match[1];
  }

  throw new Error('ObjectStorage: failed to resolve OSS region from endPoint');
}
